"use client";

import { BANNER_CONSTANTS } from "@/shared/config/ui-constants";
import { Skeleton } from "@/shared/ui/data-display/skeleton";

/**
 * KAN-1100: скелетон обязан повторять ГЕОМЕТРИЮ страницы турнира, а не намекать
 * на неё.
 *
 * Прошлая версия рисовала другую страницу: баннер фиксированной высоты
 * (`h-48 md:h-64` — 192/256px против реальных 4:1 от ширины, то есть ~300px на
 * десктопе), двухколоночную сетку `lg:grid-cols-3` с сайдбаром, которых на
 * странице нет вовсе, и другие отступы контейнера (`px-4 py-8` против
 * `px-3 sm:px-4 py-4 sm:py-8`). При подмене скелетона содержимым раскладка
 * менялась целиком — это и давало сдвиг.
 *
 * Полевой RUM за 14 дней подтверждал ровно такую картину: CLS p75 на
 * `/tournaments/[id]` — 0.263 при пороге 0.1 (максимум 0.493), при том что
 * главная держит 0.001. И сдвиги были почти исключительно на десктопе
 * (p75 0.130 против 0.002 на мобильных) — там, где `lg:grid-cols-3` вообще
 * включается, а разница высот баннера максимальна.
 *
 * Правило для правок: меняется геометрия `TournamentDetailHeader` или
 * `TournamentDetailTabs` — меняется и этот файл. Скелетон, разошедшийся с
 * содержимым, не «чуть неточен»: он ровно тот сдвиг, ради предотвращения
 * которого его показывают.
 */
export const TournamentDetailPageSkeleton = () => {
  return (
    // Контейнер повторяет `page.tsx`: те же отступы, иначе контент поедет вбок.
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
      {/* Блок шапки: та же обёртка `mb-8`, что в TournamentDetailHeader. */}
      <div className="mb-8">
        {/* Баннер: аспект и min-height один в один с шапкой — именно здесь
            раньше терялось ~44px на десктопе. */}
        <div
          className="w-full overflow-hidden rounded-[14px] min-h-[200px] sm:min-h-[240px] md:min-h-[280px]"
          style={{ aspectRatio: BANNER_CONSTANTS.ASPECT_RATIO }}
        >
          <Skeleton className="h-full w-full" />
        </div>

        {/* Ряд «Назад к турнирам» + действия: mt-4, высота кнопки size="sm". */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-8 w-32" />
        </div>
      </div>

      {/* Табы: на мобильных — один селект во всю ширину, от md — строка вкладок
          с нижней границей. Повторяем обе ветки, а не только десктопную. */}
      <div className="mb-4 md:mb-8">
        <div className="md:hidden mb-4">
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="hidden md:block">
          <div className="flex flex-wrap gap-1 border-b border-border">
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
      </div>

      {/* Содержимое вкладки: одна колонка, как на странице. Карточка описания
          турнира — самый частый первый блок. */}
      <div className="space-y-6">
        <div className="rounded-[22px] border px-5 sm:px-6 py-5">
          <Skeleton className="h-5 w-56 mb-3" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-5/6 mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        <div className="rounded-[22px] border px-5 sm:px-6 py-5">
          <Skeleton className="h-5 w-40 mb-3" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
};
